import RoomTreeViewModel from "../modelTreeViews/RoomTreeviewModel";
import BaseModel, { executePaste_Parameters } from "./BaseModel";
import BaseModelMap from "./BaseModelMap";
import BaseObject from "./BaseObject";
import Cabinet from "./Cabinet";
import Consumer from "./Consumer";
import type Floor from "./Floor";
import Page, { mmToPixel } from "./Page";
import type Button from "./Button";
import { copyAllProperties, sortArrayByProperty } from "./utils";
import Point from "./Point";
import Logger from "../Logger";
import Fuse from "./Fuse";
import { ChannelType, channelTypeInfo } from "./ChannelType";

const logger = new Logger();

//////////////////////////////////////////////////
import ObjectFactory from "./ObjectFactory";
import GroupAddress from "./GroupAdress";
import BoundaryRect from "./BoundaryRect";
import RoomToFloorTransform from "./RoomToFloorTransform";
import { itx } from "../translationItx/itx";
import Memo from "./Memo";
import Channel from "./Channel";
import ConnectableObject from "./ConnectableObject";
import { ConnectionType } from "./Connection";
import { ApplicationMode } from "./SettingsEnums";
import Group from "./Group";
import type { C1_Room } from "../../ConceptOne/src/model/C1_Room";
import { C1_Wall } from "../../ConceptOne/src/model/C1_Wall";
ObjectFactory.registerClass("Room", (data?: any, parent?: BaseModel) => {
  return new Room(data, parent);
});
//////////////////////////////////////////////////

export default class Room extends BaseModel {
  override get c1(): C1_Room | undefined {
    return super.c1 as C1_Room | undefined;
  }

  override set c1(newValue: C1_Room | undefined) {
    super.c1 = newValue;
  }

  public get className(): string {
    return "Room"; // no itx
  }

  getParentCollectionName(): string | undefined {
    return "rooms"; // no itx
  }

  public get isRoom(): boolean {
    return true;
  }

  _page: Page | undefined = undefined;
  consumers = new BaseModelMap<Consumer>(Consumer, this, "cns"); // no itx
  buttons!: BaseModelMap<Button>;
  // buttons = new BaseModelMap<Button>(Button, this, "s");
  groups = new BaseModelMap<Group>(Group, this, "g");
  /** Ordered polygon segments used by Concept One room geometry. */
  walls = new BaseModelMap<C1_Wall>(C1_Wall, this, "walls");

  _cabinetId = "";
  _groupAddressesId?: string;

  waitForNewCabinet = false;

  // -----------------------------------------------------------------------------
  // tRtoF
  // -----------------------------------------------------------------------------
  private _tRtoF: RoomToFloorTransform | undefined;
  get tRtoF(): RoomToFloorTransform | undefined {
    return this._tRtoF;
  }

  set tRtoF(newValue: RoomToFloorTransform | undefined) {
    if (this._tRtoF === newValue) return;
    this.beforeChange("tRtoF");
    this._tRtoF = newValue;
    this.changed("tRtoF");
  }

  // -----------------------------------------------------------------------------
  // br
  // -----------------------------------------------------------------------------
  private _br = new BoundaryRect();
  get br(): BoundaryRect {
    return this._br;
  }

  set br(newValue: BoundaryRect) {
    if (this._br === newValue) return;
    this.beforeChange("br");
    this._br = newValue;
    this.changed("br");
  }

  // -----------------------------------------------------------------------------

  //
  // 5 mm "Padding" around the roomBoundary in floorplan
  // will be the area cut and clipped for the room bitmap
  //
  // so padding size in the pageplan will vary with the scale of the roomplan relativ to th floorpaln
  //

  get paddingForBoundingRectInFloorSpace(): number {
    return 10 * mmToPixel;
  }

  public get boundingRectInFloorSpaceWithPadding(): BoundaryRect {
    const d = this.paddingForBoundingRectInFloorSpace;
    const xbr = this.boundingRectInFloorSpace;

    return new BoundaryRect(new Point(xbr.min.x - d, xbr.min.y - d), new Point(xbr.max.x + d, xbr.max.y + d));
  }

  public get boundingRectInFloorSpace(): BoundaryRect {
    const roomPage = this.page;
    if (this.br.width == 0 && this.roomtoFloorDrawingTransform && roomPage) {
      const w = roomPage.printableArea.width * 10 * mmToPixel * this.roomtoFloorDrawingTransform.scale;
      const h = roomPage.printableArea.width * 10 * mmToPixel * this.roomtoFloorDrawingTransform.scale;

      const p1 = new Point(this.roomtoFloorDrawingTransform.position.x, this.roomtoFloorDrawingTransform.position.y);

      const p2 = new Point(
        this.roomtoFloorDrawingTransform.position.x + w,
        this.roomtoFloorDrawingTransform.position.y + h,
      );

      this.br = new BoundaryRect(p1, p2);
    }

    return this.br;
  }

  public set boundingRectInFloorSpace(value: BoundaryRect) {
    this.br = value;
  }

  public get roomtoFloorDrawingTransform(): RoomToFloorTransform | undefined {
    if (this.isFloorGeneralRoom) {
      //
      // Room FloorGeneral allway has a neutral transform
      //
      const newRoomToFloorTransform = new RoomToFloorTransform();
      return newRoomToFloorTransform;
    }
    return this.tRtoF;
  }
  public set roomtoFloorDrawingTransform(value: RoomToFloorTransform | undefined) {
    if (!this.isFloorGeneralRoom) {
      this.tRtoF = value;
    } else {
      this.tRtoF = undefined;
    }
  }

  get isVisibleInFloorPlan(): boolean {
    return this.isFloorGeneralRoom || this.tRtoF != undefined;
  }

  getScaleFloorspaceToBitmapSpace(floorBitmap: HTMLImageElement): number {
    const page = this.page;
    const floorPage = (this.parent as Floor)?.page;

    if (floorPage) {
      const scale = floorPage.backgroundImagePosition.scale;

      if (floorPage.backgroundImagePosition.width) {
        const w1 = floorPage.backgroundImagePosition.width;
        const w2 = floorBitmap.width;
        const scale2 = w1 / w2;

        return 1 / (scale2 * scale);
      } else {
        return 1 / scale;
      }
    }
    return 1;
  }

  getBoundingRectInFloorBitmapSpace(floorBitmap: HTMLImageElement): BoundaryRect | undefined {
    const page = this.page;
    const floorPage = (this.parent as Floor)?.page;

    if (page && this.boundingRectInFloorSpace && floorPage) {
      const boundingRect = this.boundingRectInFloorSpace;

      const scale = this.getScaleFloorspaceToBitmapSpace(floorBitmap);

      const x = floorPage.backgroundImagePosition.posX;
      const y = floorPage.backgroundImagePosition.posY;

      const res = new BoundaryRect(
        new Point((boundingRect.min.x - x) * scale, (boundingRect.min.y - y) * scale),

        new Point((boundingRect.max.x - x) * scale, (boundingRect.max.y - y) * scale),
      );
      return res;
    }

    return undefined;
  }

  getPersistentProperties(): string[] {
    return [
      "oref:page", // no itx
      "consumers", // no itx
      "buttons", // no itx
      "groups", // no itx
      "walls", // no itx
      "_cabinetId", // no itx
      "_groupAddressesId", // no itx
      "tRtoF", // no itx
      "br", // no itx
      ...super.getPersistentProperties(),
    ];
  }

  constructor(data?: any, parent?: BaseModel) {
    super(data, parent);
    copyAllProperties(data, this);
    this.treeViewModel = new RoomTreeViewModel(this);

    const ButtonClass = require("./Button").default;
    this.buttons = new BaseModelMap<Button>(ButtonClass, this, "s");
  }

  set page(newValue: Page | undefined) {
    this.beforeChange("page");
    if (newValue) {
      newValue.propertyId = "p";
    }
    this._page = newValue;

    this.changed("page");
  }

  get page(): Page | undefined {
    if (!state.isSaveingToEstadaStorage && this.isFloorGeneralRoom) {
      return this.parentFloor?.page;
    } else {
      return this._page;
    }
  }

  getAllLights(): Array<Consumer> {
    const res = new Array<Consumer>();
    this.consumers.sortBy("tag").forEach((con) => {
      if (channelTypeInfo(con.channelType).isLight) {
        res.push(con);
      }
    });
    return res;
  }

  getOrCreatePage(): Page {
    if (this.isFloorGeneralRoom) {
      const floor = this.parentFloor;
      if (floor) {
        return floor.getOrCreatePage();
      } else {
        logger.error("No parent floor"); // no itx
        return new Page({}, this);
      }
    } else {
      if (this.page == undefined) {
        this.page = new Page({}, this);
      }
      return this.page;
    }
  }

  fromJSON(data: any, parent: BaseObject | undefined = undefined): void {
    if (data.page) {
      this.page = new Page({}, this);
    }
    super.fromJSON(data, parent);
  }

  loadFromEstadaStorage = async (
    data: any,
    parent: BaseModel | undefined,
    loadData: (id: string | undefined, parentId: string | undefined) => any,
  ): Promise<boolean> => {
    // create instances of optional references

    const orefPage = data["oref:page"]; // no itx
    if (orefPage) {
      this.page = new Page({ id: orefPage }, this);
      logger.log(`load page ${orefPage}, created`);
    }

    // normal loading

    const res = await super.loadFromEstadaStorage(data, parent, loadData);

    const orefTrans = data["tRtoF"]; // no itx
    if (orefTrans) {
      logger.log(`load trans`);
      const orefTrans = data["tRtoF"]; // no itx
      this.tRtoF = new RoomToFloorTransform();
      this.tRtoF.fromJSON(orefTrans);
    }

    return res;
  };

  delete(): Array<BaseObject> {
    this.isDeleting = true;
    const floor = this.parent as Floor | undefined;

    let res = new Array<BaseObject>();

    res = res.concat(this.consumers.deleteAllItems());
    res = res.concat(this.buttons.deleteAllItems());
    res = res.concat(this.groups.deleteAllItems());

    //delete page
    if (this.page) {
      res = res.concat(this.page.delete());
    }

    // delete this
    res = res.concat(super.delete());
    this.recalculateRemainingWallTopology(floor);

    return res;
  }

  undelete() {
    super.undelete();
    const floor = this.parent as Floor;
    if (floor) {
      this.c1?.recalculateFloorWallTypes();
    }
  }

  /** Reclassifies surviving walls after this room has left its floor collection. */
  private recalculateRemainingWallTopology(floor: Floor | undefined): void {
    if (!floor) return;
    for (const room of floor.rooms) {
      if (!room.c1 || room === this) continue;
      room.c1.recalculateFloorWallTypes();
      return;
    }
  }
  canBeDeletedInteractive(): boolean {
    if (state.settings.applicationMode === ApplicationMode.ConceptOne) return true;
    if (this.buttons.size == 0 && this.consumers.size == 0) {
      return true;
    } else {
      return false;
    }
  }

  getDeleteConfirmText(): string | undefined {
    if (!this.canBeDeletedInteractive()) {
      const T1 = this.fullTag;
      const T2 = `${this.buttons.size} ${itx("components.plural.Button")}${itx("and")} ${this.consumers.size} ${itx("components.plural.Consumer")}`;

      return state.translate("Floor.T1StillHasT2", "T1 hat noch T2", {
        T1: `${T1}`,
        T2: T2,
      });
    } else {
      return super.getDeleteConfirmText();
    }
  }

  canBeEditedInteractive(): boolean {
    return false;
  }

  get cabinetId(): string {
    return this._cabinetId;
  }
  set cabinetId(newValue: string) {
    this.beforeChange("cabinetId");
    this._cabinetId = newValue;

    this.changed("cabinetId");
  }

  // return cabinet of room
  // maybe cabinet of floor if no own cabimet is set
  get ownCabinet(): Cabinet | undefined {
    if (this.cabinetId != "") {
      return this.state.objectRegistry.getObject(this.cabinetId);
    } else {
      return undefined;
    }
  }

  // return cabinet of room
  // maybe cabinet of floor if no own cabimet is set
  get cabinet(): Cabinet | undefined {
    const oc = this.ownCabinet;
    if (oc) {
      return oc;
    } else {
      const floor = this.parent as Floor;
      return floor?.cabinet;
    }
  }

  get groupAddress(): GroupAddress | undefined {
    return this.state.objectRegistry.getObject(this._groupAddressesId);
  }

  set groupAddress(newValue: GroupAddress | undefined) {
    if (this._groupAddressesId === newValue?._id) return;
    this.beforeChange("groupAddress");
    this._groupAddressesId = newValue?._id;
    this.changed("groupAddress");
  }

  addConsumer(consumer: Consumer) {
    consumer.parent = this;
    this.consumers.add(consumer);
  }

  removeConsumer(consumer: Consumer) {
    this.consumers.remove(consumer);
    consumer.parent = undefined;
  }

  addMemo(memo: Memo) {
    const page = this.getOrCreatePage();
    page.addMemo(memo);
  }

  ////////////////////////////////////////////////////////////////////

  addButton(button: Button) {
    button.parent = this;
    this.buttons.add(button);
  }

  removeButton(button: Button) {
    this.buttons.remove(button);
  }

  getAllTagsFromSameTypeWithoutOwn(): string[] | undefined {
    const parentFloor = this.parent as Floor;
    //
    // MWE 9.12.2025 - Unique tags are for Consumers and Buttons only
    // NOT for Rooms !!!
    //
    if (parentFloor) {
      const res = new Array<string>();
      parentFloor.rooms.forEach((room1) => {
        if (this.tag != room1.tag) {
          res.push(room1.tag);
        }
      });
      return res;
    }
    return undefined;
  }

  getParentChildrenGroup(): Array<BaseModel> | undefined {
    return (this.parent as Floor)?.rooms.sortBy("tag");
  }

  findConnectableActorChannel(connectionType: ConnectionType, partnerObject: ConnectableObject): Channel | undefined {
    let res: Channel | undefined;

    logger.log(`findConnectableChannel cabinent`);
    // only for decentral actors
    const sortedButtons = this.buttons.sortBy("tagOrName").filter((b) => b.isDecentralActor);
    sortedButtons.forEach((button) => {
      const actorChannels = button.channels.asArray().filter((c) => c.isActorChannel);
      actorChannels.forEach((channel) => {
        if (channel.isConnectable(connectionType, partnerObject)) {
          res = channel;
          return;
        }
      });
      if (res != undefined) {
        return;
      }
    });

    return res;
  }

  ////////////////////////////////////////////////
  ////Fuses
  ///////////////////
  conectAll230ConsumersWihFuse(fuse: Fuse): boolean {
    let res = false;

    this.consumers.forEach((consumer) => {
      if (fuse.isConnectable(consumer)) {
        // we can connect this consumer with the fuse
        if (
          !channelTypeInfo(consumer.channelType).isRollo &&
          consumer.channelType != ChannelType.suply400V &&
          !channelTypeInfo(consumer.channelType).isHeating
        ) {
          const doable = consumer.connectWithFuse(fuse);
          if (doable) {
            res = doable;
          }
        }
      }
    });

    return res;
  }

  //////////////////////////////////////////////////////
  ///////////// for copy
  getExtendedCopyCollection(startItem: BaseModel): Array<BaseModel> {
    let res: Array<BaseModel> = this.consumers.asArray();

    res = res.concat(this.buttons.asArray());
    res = res.concat(this.groups.asArray());

    if (this.page) {
      res.push(this.page);
    }
    if (this.cabinet) {
      res.push(this.cabinet);
    }
    return res;
  }
  //////////////////////////////////////////////////////
  ///////////// for paste
  mapAfterCopy() {
    if (!this.mapAfterCopyDone) {
      super.mapAfterCopy();
      this.consumers.mapAfterCopy();
      this.buttons.mapAfterCopy();
      this.groups.mapAfterCopy();
      this.page?.mapAfterCopy();

      if (this.cabinetId) {
        this.cabinetId = state.objectMapping.getMappedId(this.cabinetId) || "";
      }
    }
  }
  async pasteItem(params: { item: BaseModel; allItems: Array<BaseModel> }): Promise<Array<BaseModel>> {
    const { item } = params;

    if (item.className == "Consumer") {
      if (this.page) {
      }
      const consumer = item as Consumer;
      var res: Array<BaseModel> = await consumer.executePaste({
        parent: this,
        isCopyRootObj: true,
      });

      return res;
    } else if (item.className == "Button") {
      const button = item as Button;
      var res: Array<BaseModel> = await button.executePaste({
        parent: this,
        isCopyRootObj: true,
      });
      if (this.page) {
        const pos = button.position;
        button.position = new Point(pos.x + 50, pos.y + 50);
      }
      return res;
    } else if (item.className == "Actor") {
      return (await this.cabinet?.pasteItem(params)) || [];
    } else if (item.className == "Memo") {
      const memo = item as Memo;
      var res: Array<BaseModel> = await memo.executePaste({
        parent: this,
        isCopyRootObj: true,
      });

      return res;
    } else {
      return (await this.parent?.pasteItem(params)) || [];
    }
  }

  async executePaste(params: executePaste_Parameters): Promise<Array<BaseModel>> {
    const { parent, isCopyRootObj } = params;
    const floor = parent as Floor;
    let undoObjects: Array<BaseModel> = [this];

    // set parent
    this.parent = floor;
    floor.rooms.add(this);
    // calc tag
    if ((isCopyRootObj || state.projectMetadata.uniqueTagsForProject) && this.tag != "") {
      this.tag = "";
      this.updateTagInPaste(floor.rooms);
      if (isCopyRootObj) {
        this.updateNameInPaste(floor.rooms);
      }
    }

    if (this.ownCabinet) {
      undoObjects = await this.ownCabinet.executePaste({ parent: state.cabinets, isCopyRootObj: false });
      this.ownCabinet.updateTagInPaste(state.cabinets);
    }
    // paste Consumers
    for (const consumer of this.consumers.asArray()) {
      undoObjects.push(...(await consumer.executePaste({ parent: this, isCopyRootObj: false })));
    }

    //paste Buttons
    for (const button of this.buttons.asArray()) {
      undoObjects.push(...(await button.executePaste({ parent: this, isCopyRootObj: false })));
    }

    for (const group of this.groups.asArray()) {
      undoObjects.push(...(await group.executePaste({ parent: this, isCopyRootObj: false })));
    }

    //paste Page
    const page = this.page;
    if (page != undefined) {
      undoObjects.concat(await page.executePaste({ parent: this, isCopyRootObj: false }));
    }

    return undoObjects;
  }

  pointAlreadyUsed(p: Point): boolean {
    const page = this.page;
    if (page) {
      return page.pointAlreadyUsed(p);
    }

    return false;
  }

  raiseOnChange(changedProperty: string | any | undefined = undefined, eventSource?: string, uiOnly?: boolean) {
    super.raiseOnChange(changedProperty, eventSource, uiOnly);

    if (changedProperty == "childCollection") {
      if (this.tag == "" && this.name == "") {
        //
        // FloorGeneral Room is hidden in the treeview,
        // if it has no consumers or buttons
        //
        // to we have to refresh the childCollection of the floor
        // if a childCollection of the FloorGeneral Room is changed
        //

        this.parent?.raiseOnChange("childCollection", undefined, true);
      }
    }
  }

  get isFloorGeneralRoom(): boolean {
    return this.tag == "" && this.name == "";
  }

  get parentFloor(): Floor | undefined {
    if (this.parent) {
      if (this.parent?.className == "Floor") {
        return this.parent as Floor;
      }
    }
    return undefined;
  }

  cumulateChildObjectsErrorStatus(): boolean {
    let res = this.consumers.cumulateChildObjectsErrorStatus();
    res = this.buttons.cumulateChildObjectsErrorStatus() || res;
    res = this.page?.hasErrors || this.page?.cumulateChildObjectsErrorStatus() || res;
    return res;
  }

  consistencyCheck(parent: BaseModel) {
    super.consistencyCheck(parent);

    if (this.cabinetId) {
      const cab = this.cabinet;
      if (!cab) {
        this.logConsistencyError(`cabinet with id ${this.cabinetId} not found`); // no itx
        this.cabinetId = "";
      }
    }

    if (this.isFloorGeneralRoom && this._page) {
      this.logConsistencyError(`FloorGeneral Room must not have own page, fixed.`); // no itx
      this._page = undefined;
    }

    this.consumers.consistencyCheck(this);
    this.buttons.consistencyCheck(this);
    this.groups.consistencyCheck(this);
    this._page?.consistencyCheck(this);
  }

  mergeAllFrom(fromRoom: Room) {
    const allMergeConsumers = fromRoom.consumers.asArray();
    allMergeConsumers.forEach((consumer) => consumer.moveToNewParent(this));

    const allMergeButtons = fromRoom.buttons.asArray();
    allMergeButtons.forEach((button) => button.moveToNewParent(this));
  }

  allConsumers(): Array<Consumer> {
    let res = this.consumers.asArray();

    this.groups.forEach((group) => {
      res = res.concat(group.allConsumers);
    });

    return sortArrayByProperty(res, "fullTag");
  }

  get allButtons(): Array<Button> {
    let res = this.buttons.asArray();
    this.groups.forEach((group) => {
      res = res.concat(group.allButtons);
    });
    return sortArrayByProperty(res, "fullTag");
  }
}
